import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { ArrowLeft, Loader2, AlertCircle } from 'lucide-react';
import { fetchJob, updateJob, fetchCompanies } from '@/lib/api';
import { JOB_STATUSES, JOB_TYPES } from '@/types';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { RichTextEditor } from '@/components/ui/rich-text-editor';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { SkillsMultiSelect } from '@/components/ui/skills-multi-select';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

const REMOTE_TYPES = ['on_site', 'hybrid', 'fully_remote'] as const;
const PRIORITIES = ['low', 'normal', 'high', 'urgent'] as const;
const CURRENCIES = ['USD', 'EUR', 'GBP', 'ZAR', 'AUD'] as const;

const optionalNumber = z.preprocess(
  (val) => (val === '' || val === null || val === undefined ? undefined : Number(val)),
  z.number().min(0).optional(),
);

const jobSchema = z.object({
  title: z.string().min(1, 'Title is required').max(200, 'Title must be less than 200 characters'),
  description: z.string().optional(),
  requirements: z.string().optional(),
  location: z.string().optional(),
  jobType: z.enum(JOB_TYPES).optional().nullable().or(z.literal('')),
  remoteType: z.enum(REMOTE_TYPES).optional().nullable().or(z.literal('')),
  salaryMin: optionalNumber,
  salaryMax: optionalNumber,
  salaryCurrency: z.enum(CURRENCIES).optional().or(z.literal('')).default('USD'),
  salaryFrequency: z.enum(['hourly', 'weekly', 'monthly', 'annual'] as const).optional().or(z.literal('')).default('annual'),
  status: z.enum(JOB_STATUSES).optional().or(z.literal('')).default('draft'),
  priority: z.enum(PRIORITIES).optional().or(z.literal('')).default('normal'),
  positionsAvailable: z.preprocess(
    (val) => (val === '' || val === null || val === undefined ? 1 : Number(val)),
    z.number().int().min(1).default(1),
  ),
  deadline: z.string().optional().nullable(),
  experienceMin: optionalNumber,
  experienceMax: optionalNumber,
  companyId: z.string().uuid().optional().nullable().or(z.literal('')),
  candidateSafeTitle: z.string().optional(),
  candidateSafeDescription: z.string().optional(),
});

type JobFormData = z.infer<typeof jobSchema>;

function EditJobSkeleton() {
  return (
    <div className="space-y-6 max-w-4xl mx-auto">
      <Skeleton className="h-6 w-32" />
      <div className="space-y-2">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-5 w-96" />
      </div>
      <Skeleton className="h-64 w-full" />
      <Skeleton className="h-48 w-full" />
      <Skeleton className="h-48 w-full" />
    </div>
  );
}

export function EditJobPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const [skills, setSkills] = useState<string[]>([]);

  const {
    data: job,
    isLoading,
    error: fetchError,
  } = useQuery({
    queryKey: ['job', id],
    queryFn: () => fetchJob(id!),
    enabled: !!id,
  });

  const { data: companies = [] } = useQuery({
    queryKey: ['companies'],
    queryFn: () => fetchCompanies(),
  });

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    reset,
    formState: { errors },
  } = useForm<JobFormData>({
    resolver: zodResolver(jobSchema),
    defaultValues: {
      status: 'draft',
      priority: 'normal',
      salaryCurrency: 'USD',
      salaryFrequency: 'annual',
      positionsAvailable: 1,
    },
  });

  // Radix Select fields are not native inputs, so register them explicitly.
  // Without this, loaded values can be displayed as placeholders and fail Zod validation on submit.
  useEffect(() => {
    register('companyId');
    register('status');
    register('priority');
    register('remoteType');
    register('jobType');
    register('salaryCurrency');
    register('salaryFrequency');
  }, [register]);

  // Populate form when job data is loaded
  useEffect(() => {
    if (job) {
      reset({
        title: job.title,
        description: job.description || '',
        requirements: job.requirements || '',
        location: job.location || '',
        jobType: job.jobType,
        remoteType: job.remoteType as typeof REMOTE_TYPES[number] | null,
        salaryMin: job.salaryMin ?? undefined,
        salaryMax: job.salaryMax ?? undefined,
        salaryCurrency: (job.salaryCurrency as typeof CURRENCIES[number]) || 'USD',
        salaryFrequency: (job.salaryFrequency as 'hourly' | 'weekly' | 'monthly' | 'annual') || 'annual',
        status: job.status,
        priority: job.priority as typeof PRIORITIES[number],
        positionsAvailable: job.positionsAvailable,
        deadline: job.deadline ? job.deadline.split('T')[0] : '',
        experienceMin: job.experienceMin ?? undefined,
        experienceMax: job.experienceMax ?? undefined,
        companyId: job.company?.id || '',
        candidateSafeTitle: job.candidateSafeTitle || '',
        candidateSafeDescription: job.candidateSafeDescription || '',
      });
      // Force custom Select controls to sync after reset; Radix Select is not a native input.
      setValue('companyId', job.company?.id || '', { shouldValidate: true });
      setValue('status', job.status as typeof JOB_STATUSES[number], { shouldValidate: true });
      setValue('priority', job.priority as typeof PRIORITIES[number], { shouldValidate: true });
      setValue('remoteType', job.remoteType as typeof REMOTE_TYPES[number] | null, { shouldValidate: true });
      setValue('jobType', job.jobType as typeof JOB_TYPES[number] | null, { shouldValidate: true });
      setValue('salaryCurrency', (job.salaryCurrency as typeof CURRENCIES[number]) || 'USD', { shouldValidate: true });
      setValue('salaryFrequency', (job.salaryFrequency as 'hourly' | 'weekly' | 'monthly' | 'annual') || 'annual', { shouldValidate: true });
      setSkills(job.requiredSkills || []);
    }
  }, [job, reset, setValue]);

  const updateMutation = useMutation({
    mutationFn: (data: Parameters<typeof updateJob>[1]) => updateJob(id!, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['job', id] });
      queryClient.invalidateQueries({ queryKey: ['jobs'] });
      navigate(`/jobs/${id}`);
    },
    onError: (err) => {
      setError(err instanceof Error ? err.message : 'Failed to update job');
    },
  });

  const onSubmit = (data: JobFormData) => {
    setError(null);
    updateMutation.mutate({
      ...data,
      salaryCurrency: data.salaryCurrency || job?.salaryCurrency || 'USD',
      salaryFrequency: data.salaryFrequency || job?.salaryFrequency || 'annual',
      status: data.status || job?.status || 'draft',
      priority: data.priority || job?.priority || 'normal',
      jobType: data.jobType || job?.jobType || undefined,
      remoteType: data.remoteType || job?.remoteType || undefined,
      salaryMin: data.salaryMin ?? undefined,
      salaryMax: data.salaryMax ?? undefined,
      experienceMin: data.experienceMin ?? undefined,
      experienceMax: data.experienceMax ?? undefined,
      companyId: data.companyId || job?.company?.id || undefined,
      deadline: data.deadline || undefined,
      requiredSkills: skills,
    });
  };

  if (isLoading) {
    return <EditJobSkeleton />;
  }

  if (fetchError || !job) {
    return (
      <div className="flex flex-col items-center justify-center h-96">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-text-primary">Job Not Found</h2>
          <p className="text-text-tertiary mt-2">
            The job you are trying to edit does not exist or has been removed.
          </p>
          <Button asChild className="mt-4">
            <Link to="/jobs">Back to Jobs</Link>
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-4xl mx-auto">
      {/* Back Button */}
      <div>
        <Link
          to={`/jobs/${id}`}
          className="inline-flex items-center gap-2 text-sm text-text-tertiary hover:text-text-primary"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Job
        </Link>
      </div>

      {/* Page Header */}
      <div>
        <h1 className="text-2xl font-bold text-text-primary">Edit Job</h1>
        <p className="text-text-secondary mt-1">
          Update the details for "{job.title}"
        </p>
      </div>

      <form onSubmit={handleSubmit(onSubmit, () => setError('Please check the form for errors before saving.'))} className="space-y-6">
        {error && (
          <div className="flex items-center gap-2 p-3 text-sm text-red-600 bg-red-50 rounded-md">
            <AlertCircle className="h-4 w-4 flex-shrink-0" />
            <span>{error}</span>
          </div>
        )}

        {/* Basic Information */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Basic Information</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="title">Job Title *</Label>
              <Input
                id="title"
                placeholder="e.g., Senior Software Engineer"
                {...register('title')}
              />
              {errors.title && (
                <p className="text-sm text-red-600">{errors.title.message}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="companyId">Company</Label>
              <Select
                value={watch('companyId') || ''}
                onValueChange={(value) => setValue('companyId', value === '_none' ? '' : value, { shouldDirty: true, shouldValidate: true })}
              >
                <SelectTrigger id="companyId">
                  <SelectValue placeholder="Select company (optional)" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="_none">No company</SelectItem>
                  {companies.map((company) => (
                    <SelectItem key={company.id} value={company.id}>
                      {company.name}{company.industry ? ` (${company.industry})` : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="status">Status</Label>
                <Select
                  value={watch('status')}
                  onValueChange={(value) => setValue('status', value as typeof JOB_STATUSES[number], { shouldDirty: true, shouldValidate: true })}
                >
                  <SelectTrigger id="status">
                    <SelectValue placeholder="Select status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="draft">Draft</SelectItem>
                    <SelectItem value="open">Open</SelectItem>
                    <SelectItem value="paused">Paused</SelectItem>
                    <SelectItem value="filled">Filled</SelectItem>
                    <SelectItem value="cancelled">Cancelled</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="priority">Priority</Label>
                <Select
                  value={watch('priority')}
                  onValueChange={(value) => setValue('priority', value as typeof PRIORITIES[number], { shouldDirty: true, shouldValidate: true })}
                >
                  <SelectTrigger id="priority">
                    <SelectValue placeholder="Select priority" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Low</SelectItem>
                    <SelectItem value="normal">Normal</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                    <SelectItem value="urgent">Urgent</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="positionsAvailable">Positions Available</Label>
                <Input
                  id="positionsAvailable"
                  type="number"
                  min="1"
                  {...register('positionsAvailable')}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="deadline">Deadline</Label>
                <Input
                  id="deadline"
                  type="date"
                  {...register('deadline')}
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Location & Type */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Location & Type</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="location">Location</Label>
                <Input
                  id="location"
                  placeholder="e.g., Cape Town, South Africa"
                  {...register('location')}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="remoteType">Remote Type</Label>
                <Select
                  value={watch('remoteType') || ''}
                  onValueChange={(value) => setValue('remoteType', value as typeof REMOTE_TYPES[number], { shouldDirty: true, shouldValidate: true })}
                >
                  <SelectTrigger id="remoteType">
                    <SelectValue placeholder="Select remote type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="on_site">On-site</SelectItem>
                    <SelectItem value="hybrid">Hybrid</SelectItem>
                    <SelectItem value="fully_remote">Remote</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="jobType">Job Type</Label>
              <Select
                value={watch('jobType') || ''}
                onValueChange={(value) => setValue('jobType', value as typeof JOB_TYPES[number], { shouldDirty: true, shouldValidate: true })}
              >
                <SelectTrigger id="jobType">
                  <SelectValue placeholder="Select job type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="full_time">Full Time</SelectItem>
                  <SelectItem value="part_time">Part Time</SelectItem>
                  <SelectItem value="contract">Contract</SelectItem>
                  <SelectItem value="internship">Internship</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Compensation */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Compensation</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="space-y-2">
                <Label htmlFor="salaryMin">Minimum Salary</Label>
                <Input
                  id="salaryMin"
                  type="number"
                  min="0"
                  placeholder="e.g., 50000"
                  {...register('salaryMin')}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="salaryMax">Maximum Salary</Label>
                <Input
                  id="salaryMax"
                  type="number"
                  min="0"
                  placeholder="e.g., 80000"
                  {...register('salaryMax')}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="salaryCurrency">Currency</Label>
                <Select
                  value={watch('salaryCurrency')}
                  onValueChange={(value) => setValue('salaryCurrency', value as typeof CURRENCIES[number], { shouldDirty: true, shouldValidate: true })}
                >
                  <SelectTrigger id="salaryCurrency">
                    <SelectValue placeholder="Select currency" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="USD">USD</SelectItem>
                    <SelectItem value="EUR">EUR</SelectItem>
                    <SelectItem value="GBP">GBP</SelectItem>
                    <SelectItem value="ZAR">ZAR</SelectItem>
                    <SelectItem value="AUD">AUD</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="salaryFrequency">Pay Frequency</Label>
                <Select
                  value={watch('salaryFrequency')}
                  onValueChange={(value) => setValue('salaryFrequency', value as 'hourly' | 'weekly' | 'monthly' | 'annual', { shouldDirty: true, shouldValidate: true })}
                >
                  <SelectTrigger id="salaryFrequency">
                    <SelectValue placeholder="Select frequency" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="hourly">Hourly</SelectItem>
                    <SelectItem value="weekly">Weekly</SelectItem>
                    <SelectItem value="monthly">Monthly</SelectItem>
                    <SelectItem value="annual">Annual</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Experience & Skills */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Experience & Skills</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="experienceMin">Minimum Experience (years)</Label>
                <Input
                  id="experienceMin"
                  type="number"
                  min="0"
                  placeholder="e.g., 2"
                  {...register('experienceMin')}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="experienceMax">Maximum Experience (years)</Label>
                <Input
                  id="experienceMax"
                  type="number"
                  min="0"
                  placeholder="e.g., 5"
                  {...register('experienceMax')}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Required Skills</Label>
              <SkillsMultiSelect
                skills={skills}
                onSkillsChange={setSkills}
                placeholder="Type a skill and press Enter..."
              />
            </div>
          </CardContent>
        </Card>

        {/* Description & Requirements */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Description & Requirements</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label>Job Description</Label>
              <RichTextEditor
                value={watch('description') || ''}
                onChange={(html) => setValue('description', html)}
                placeholder="Describe the role, responsibilities, and what makes it exciting..."
              />
            </div>

            <div className="space-y-2">
              <Label>Requirements</Label>
              <RichTextEditor
                value={watch('requirements') || ''}
                onChange={(html) => setValue('requirements', html)}
                placeholder="List the required qualifications, skills, and experience..."
              />
            </div>
          </CardContent>
        </Card>

        {/* Candidate-Facing Version (anonymised) */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Candidate-Facing Version</CardTitle>
            <p className="text-sm text-text-secondary mt-1">
              Shown to candidates on WhatsApp and on the public job board — the client company is
              never revealed here. Leave blank to auto-generate (the company name is removed
              automatically).
            </p>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="candidateSafeTitle">Safe Job Title</Label>
              <Input
                id="candidateSafeTitle"
                placeholder="Auto-generated from the job title if left blank"
                {...register('candidateSafeTitle')}
              />
            </div>
            <div className="space-y-2">
              <Label>Safe Job Description</Label>
              <RichTextEditor
                value={watch('candidateSafeDescription') || ''}
                onChange={(html) => setValue('candidateSafeDescription', html)}
                placeholder="Auto-generated from the description if left blank"
              />
            </div>
          </CardContent>
        </Card>

        {/* Actions */}
        <div className="flex justify-end gap-4">
          <Button type="button" variant="outline" asChild>
            <Link to={`/jobs/${id}`}>Cancel</Link>
          </Button>
          <Button type="submit" disabled={updateMutation.isPending}>
            {updateMutation.isPending ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Saving...
              </>
            ) : (
              'Save Changes'
            )}
          </Button>
        </div>
      </form>
    </div>
  );
}
